import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { ReviewSourceColumn } from "@/components/ReviewSourceColumn";
import { ScoreTrends } from "@/components/ScoreTrends";
import { fmtDate, todayStr } from "@/lib/format";
import { Loader2, Plus, MessageSquareQuote, Trash2, Star, BellRing } from "lucide-react";

const SOURCES = ["Google", "Expedia", "Booking.com", "Hotwire", "TripAdvisor", "Brand Website", "Front Desk", "Other"];

function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

export default function GuestComments() {
  const { user } = useAuth();
  const canAdd = can(user, ["admin", "regional", "gm", "finance", "hr"]);
  const canDelete = can(user, ["admin", "regional", "gm"]);

  const [props, setProps] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: todayStr(), source: "Google", guest_name: "", rating: "", comment: "" });

  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/properties").then((r) => { setProps(r.data); if (!propertyId && r.data[0]) setPropertyId(r.data[0].id); });
  }, []);

  const load = useCallback(() => {
    if (!propertyId) return;
    setItems(null);
    api.get("/guest-comments", { params: { property_id: propertyId } }).then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get(`/properties/${propertyId}/reviews-meta`).then((r) => { setMeta(r.data); }).catch(() => setMeta(null));
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.comment.trim()) return toast.error("Enter the review / comment");
    setSaving(true);
    try {
      await api.post("/guest-comments", { property_id: propertyId, ...form });
      toast.success("Review added");
      setForm({ date: todayStr(), source: form.source, guest_name: "", rating: "", comment: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/guest-comments/${id}`);
    load();
  };

  const saveGm = async (id, value, original) => {
    if ((value || "") === (original || "")) return;
    try {
      await api.patch(`/guest-comments/${id}`, { gm_comment: value });
      toast.success("GM comment saved");
      setItems((its) => its.map((i) => (i.id === id ? { ...i, gm_comment: value } : i)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setReplied = async (c, replied) => {
    try {
      const r = await api.patch(`/guest-comments/${c.id}`, { replied });
      setItems((its) => its.map((i) => (i.id === c.id ? r.data : i)));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openAndCheck = async (source, url) => {
    if (url) window.open(url.startsWith("http") ? url : `https://${url}`, "_blank", "noopener");
    try {
      await api.post(`/properties/${propertyId}/reviews-checked`, { source });
      setMeta((m) => ({ ...m, checks: { ...(m?.checks || {}), [source]: new Date().toISOString() } }));
    } catch { /* ignore */ }
  };

  const overdue = (meta?.sources || []).filter((s) => meta?.links?.[s]).filter((s) => {
    const d = daysSince(meta?.checks?.[s]);
    return d === null || d >= 7;
  });

  return (
    <div className="space-y-8" data-testid="guest-comments-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Guest Satisfaction</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Guest Scores</h1>
          <p className="mt-1 text-sm text-muted-foreground">Log reviews from Google, Expedia, Booking.com & more — with your GM response.</p>
        </div>
        <Select value={propertyId} onValueChange={setPropertyId}>
          <SelectTrigger className="w-56" data-testid="gc-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
          <SelectContent>{props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
        </Select>
      </div>

      {/* Latest 10 per review site (links come from Property → Review Site Links) */}
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 font-heading text-base font-bold"><BellRing className="h-4 w-4 text-primary" /> Latest reviews by site</h2>
          {overdue.length > 0 && (
            <span className="bg-[hsl(var(--warning))]/15 px-2 py-1 text-xs font-semibold text-[hsl(var(--warning))]" data-testid="review-overdue-badge">
              {overdue.length} site{overdue.length > 1 ? "s" : ""} not checked in 7+ days
            </span>
          )}
        </div>
        <p className="mb-3 text-xs text-muted-foreground">Open a site, log new reviews below, reply on the site, then tick <b>Replied</b>. Links are managed in Property → Review Site Links (* = search link awaiting Admin approval).</p>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="review-sources-grid">
          {(meta?.sources || []).filter((s) => s !== "Brand Website" || meta?.links?.[s]).map((s) => (
            <ReviewSourceColumn key={s} source={s} meta={meta} items={items || []} onOpen={openAndCheck} onReplied={setReplied} canEdit={canAdd} />
          ))}
        </div>
      </div>

      {propertyId && <ScoreTrends propertyId={propertyId} />}

      {/* Add review */}
      {canAdd && (
        <Card className="rounded-none border p-4" data-testid="gc-form">
          <div className="grid gap-3 md:grid-cols-6">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setF("date", e.target.value)} data-testid="gc-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => setF("source", v)}>
                <SelectTrigger data-testid="gc-source"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Guest Name</Label>
              <Input value={form.guest_name} onChange={(e) => setF("guest_name", e.target.value)} data-testid="gc-guest" />
            </div>
            <div className="space-y-1.5">
              <Label>Score</Label>
              <Input placeholder="e.g. 4.5/5" value={form.rating} onChange={(e) => setF("rating", e.target.value)} data-testid="gc-rating" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Review / Comment</Label>
              <Input value={form.comment} onChange={(e) => setF("comment", e.target.value)} data-testid="gc-comment" />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={submit} disabled={saving || !propertyId} data-testid="gc-submit">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Add Review
            </Button>
          </div>
        </Card>
      )}

      {/* Horizontal table */}
      {items === null ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-none border border-dashed p-12 text-center">
          <MessageSquareQuote className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No reviews logged yet.</p>
        </Card>
      ) : (
        <Card className="rounded-none border">
          <div className="overflow-auto">
            <Table data-testid="guest-scores-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Date</TableHead>
                  <TableHead className="w-32">Source</TableHead>
                  <TableHead className="w-32">Guest</TableHead>
                  <TableHead className="w-20">Score</TableHead>
                  <TableHead className="min-w-[220px]">Comment</TableHead>
                  <TableHead className="min-w-[220px]">GM Comment</TableHead>
                  <TableHead className="w-24">Replied</TableHead>
                  {canDelete && <TableHead className="w-10" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`gc-item-${c.id}`}>
                    <TableCell className="text-xs">{fmtDate(c.date)}</TableCell>
                    <TableCell className="text-xs font-medium">{c.source || "—"}</TableCell>
                    <TableCell className="text-xs">{c.guest_name || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {c.rating ? <span className="inline-flex items-center gap-1 font-semibold"><Star className="h-3 w-3 text-primary" />{c.rating}</span> : "—"}
                    </TableCell>
                    <TableCell className="text-sm">{c.comment}</TableCell>
                    <TableCell>
                      {canAdd ? (
                        <Textarea
                          rows={1}
                          defaultValue={c.gm_comment || ""}
                          placeholder="Add GM response…"
                          className="min-h-[36px] text-sm"
                          onBlur={(e) => saveGm(c.id, e.target.value, c.gm_comment)}
                          data-testid={`gc-gm-${c.id}`}
                        />
                      ) : (
                        <span className="text-sm">{c.gm_comment || "—"}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <label className="flex items-center gap-1.5 text-xs">
                        <Checkbox checked={!!c.replied} disabled={!canAdd} onCheckedChange={(v) => setReplied(c, !!v)} data-testid={`gc-replied-${c.id}`} />
                        {c.replied ? "Yes" : "No"}
                      </label>
                    </TableCell>
                    {canDelete && (
                      <TableCell>
                        <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`gc-delete-${c.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
